import React from 'react';
import { BlockType, ElementType, WidgetType } from '../../types/api/ElementType';
import Widget, { WidgetTypeDefinition } from '../../components/molecules/Widget/Widget';
import { getBlockComponent } from './BlockUtils';
import Blocks from '../../components/blocks';
import { CommentsWidget } from '../../_widgets/Comments';
import { isDynamicallyGeneratedBlockContent } from '../../types/api/Block';
import { StructuralPageToPageTreeWidget } from '../../_widgets/StructuralPageToPageTree';
import { GenericSearchListWidget } from '../../_widgets/GenericSearchList';
import { AttributeGroupPersonsWidget } from '../../_widgets/AttributeGroupPersons';
import { BlogPostListWidget } from '../../_widgets/BlogPostList';
import { TopVideoWidget } from '../../_widgets/TopVideo/TopVideoWidget';
import { ContentSearchStatisticsWidget } from '../../_widgets/ContentSearchStatistics';
import { SystemPageStatisticsWidget } from '../../_widgets/SystemPageStatistics/SystemPageStatisticsWidget';
import { PageTrashWidget } from '../../_widgets/PageTrash';
import { LatestRegistrationsWidget } from '../../_widgets/LatestRegistrations';
import { HeadingHierarchy } from '../../_lib/components/Heading/Heading';
import { PageTypeRolesWidget } from '../../_widgets/PageTypeRoles/PageTypeRolesWidget';
import { ExecutiveOrganisationChartPrintWidget } from
  '../../_widgets/ExecutiveOrganisationChartPrint/ExecutiveOrganisationChartPrint';

export default class WidgetToComponents {
  static isArticle(elementType: ElementType | BlockType | WidgetType): elementType is ElementType | WidgetType {
    return [ElementType.content, BlockType.pagelead].includes(elementType as any);
  }

  static getWidgets(widgets: WidgetTypeDefinition[], headlineHierarchy?: HeadingHierarchy) {
    // filter all possible nulls
    return widgets.map(widget => WidgetToComponents.getWidget(widget, headlineHierarchy)).filter(widget => widget);
  }

  // this is here until sections are moved to new architecture.
  // currently required for content section

  static getPureWidget(widget: WidgetTypeDefinition) {
    // Quickfix for SIBZIEL-13293
    if (isDynamicallyGeneratedBlockContent(widget.blocks[0])) {
      return null;
    }
    switch (widget.elementType) {
      case WidgetType.comments:
        return <CommentsWidget key={widget.uuid} {...(widget as any)} />;
      case WidgetType.structuralPageToPageTree:
        return <StructuralPageToPageTreeWidget key={widget.uuid} {...(widget as any)} />;
      case WidgetType.genericsearchlist:
        return <GenericSearchListWidget key={widget.uuid} {...(widget as any)} />;
      case WidgetType.attributegrouppersons:
        return <AttributeGroupPersonsWidget key={widget.uuid} {...(widget as any)} />;
      case WidgetType.blogpostlist:
        return <BlogPostListWidget key={widget.uuid} {...(widget as any)} />;
      case WidgetType.topvideo:
        return <TopVideoWidget key={widget.uuid} {...(widget as any)} />;
      case WidgetType.systempagestatistics:
        return <SystemPageStatisticsWidget key={widget.uuid} {...(widget as any)} />;
      case WidgetType.contentsearchstatistics:
        return <ContentSearchStatisticsWidget key={widget.uuid} {...(widget as any)} />;
      case WidgetType.pagetrash:
        return <PageTrashWidget key={widget.uuid} {...(widget as any)} />;
      case WidgetType.latestregistrations:
        return <LatestRegistrationsWidget key={widget.uuid} {...(widget as any)} />;
      case WidgetType.pagetyperoles:
        return <PageTypeRolesWidget key={widget.uuid} {...(widget as any)} />;
      case WidgetType.gvpprint:
        return <ExecutiveOrganisationChartPrintWidget key={widget.uuid} {...(widget as any)} />;
      default:
        return null;
    }
  }

  static getWidget(widget: WidgetTypeDefinition, headlineHierarchy?: HeadingHierarchy, nextIsFrameless = false) {
    const pureWidget = WidgetToComponents.getPureWidget(widget);

    if (pureWidget) {
      return pureWidget;
    }

    // @ts-expect-error needs work
    const blockComponents = widget.blocks.map(getBlockComponent(Blocks)).filter(block => block !== null) as any;

    // don't render the widget if it contains no blocks
    if (blockComponents.length === 0) {
      return null;
    }

    if (widget.invisible) {
      return blockComponents;
    }

    return (
      <Widget
        // key here might also lead to the same problems as BlockComponent key
        // see comment further down below in 'static getBlockComponent'
        key={widget.uuid}
        widget={widget}
        blockComponents={blockComponents}
        nextIsFrameless={nextIsFrameless}
        isArticle={WidgetToComponents.isArticle(widget.elementType)}
        headlineHierarchy={headlineHierarchy}
      />
    );
  }
}
